/** Items found for a picker (a combobox): each as the lists show it, its sort's icon and what it is beside its title. */
import { api } from "../../api/client.ts";
import type { PageItemRow, SpaceOut } from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import type { Option } from "../../lib/data.ts";
import { formatRelative } from "../../lib/format.ts";
import { spaceApi } from "../../lib/paths.ts";
import { TypeGlyph } from "./icons.tsx";

const SEARCH_LIMIT = 20;

/** An item found: its title, its sort's icon, what it is and when it came, and its entity (none: null). */
export interface ItemOption extends Option {
  entity: string | null;
}

/**
 * Item options for pickers (full-text search, optionally within an entity): each with its sort's icon (a PDF, an
 * email…) and, under its title, its entity (else its kind) and when it came, to tell alike titles apart.
 */
export async function searchItems(
  space: SpaceOut,
  q: string,
  signal: AbortSignal,
  o: { entity?: string | null; exclude?: string } = {},
): Promise<ItemOption[]> {
  const page = await api.get<PageItemRow>(
    spaceApi(space, "/items"),
    { q, entity: o.entity, limit: SEARCH_LIMIT },
    signal,
  );
  return page.data
    .filter((i) => i.id !== o.exclude)
    .map((i) => {
      const title = i.title || t("common.untitled");
      const what = space.entities.find((e) => e.id === i.entity)?.name ?? t(`itemKind.${i.kind}`);
      return {
        value: i.id,
        label: title,
        entity: i.entity ?? null,
        icon: <TypeGlyph kind={i.kind} mime={i.file?.mime} name={title} />,
        hint: `${what} · ${formatRelative(i.created_at)}`,
      };
    });
}
